import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Check, Sun, Moon, Play, Clock, Sparkles, Leaf, Lightbulb,
} from "lucide-react";
import Logo from "../components/Logo";
import api from "../lib/api";
import { exImage } from "../lib/images";
import { useAuth } from "../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DayView() {
  const { day } = useParams();
  const dayNum = parseInt(day, 10);
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [info, setInfo] = useState(null);
  const [tab, setTab] = useState("morning");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setInfo(null);
    api.get(`/program/day/${dayNum}`).then((res) => setInfo(res.data)).catch(() => {});
    window.scrollTo(0, 0);
  }, [dayNum]);

  const toggleComplete = async () => {
    setSaving(true);
    try {
      if (info.completed) {
        await api.post("/program/uncomplete", { day: dayNum });
        setInfo({ ...info, completed: false });
        toast.info("Día marcado como pendiente.");
      } else {
        await api.post("/program/complete", { day: dayNum });
        setInfo({ ...info, completed: true });
        toast.success("¡Bien hecho! Día completado 🌿".replace("🌿", ""));
      }
      await refresh();
    } finally {
      setSaving(false);
    }
  };

  if (!info) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-cream">
        <Leaf className="w-8 h-8 text-clay animate-pulse" />
      </div>
    );
  }

  const d = info.day;
  const routine = tab === "morning" ? d.morningRoutine : d.nightRoutine;

  return (
    <div className="min-h-screen bg-cream">
      <nav className="sticky top-0 z-40 backdrop-blur-xl bg-cream/80 border-b border-forest/10">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/programa" data-testid="back-to-dashboard" className="inline-flex items-center gap-2 text-sm text-forest/70 hover:text-clay transition-colors">
            <ArrowLeft className="w-4 h-4" /> Mi programa
          </Link>
          <Logo />
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
          <span className="text-xs tracking-[0.2em] uppercase font-semibold text-clay">
            Semana {d.week} · {d.weekTheme}
          </span>
          <h1 className="mt-2 font-serif text-4xl sm:text-5xl leading-tight">Día {d.day}: {d.title}</h1>
          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-forest/60">
            <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4 text-clay" /> {d.duration}</span>
            <span className="inline-flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-clay" /> {d.focus}</span>
          </div>
        </motion.div>

        {/* Video guiado de la sesión */}
        <div className="mt-8 relative rounded-3xl overflow-hidden border border-forest/10 bg-forest aspect-video" data-testid="day-video">
          <video
            key={d.day}
            controls
            playsInline
            preload="metadata"
            poster={`${BACKEND_URL}${d.poster}`}
            className="w-full h-full object-cover"
            data-testid="day-video-player"
          >
            <source src={`${BACKEND_URL}${d.video}`} type="video/mp4" />
          </video>
        </div>
        <p className="mt-3 text-sm text-forest/50 flex items-center gap-2">
          <Play className="w-4 h-4 text-clay" /> Vídeo guiado de tu sesión de hoy · sigue el ritual paso a paso.
        </p>

        {/* Exercises */}
        <section className="mt-12">
          <h2 className="font-serif text-3xl">Ejercicios de yoga facial</h2>
          <div className="mt-6 space-y-5">
            {d.exercises.map((ex, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                className="grid sm:grid-cols-[200px_1fr] gap-5 bg-white border border-forest/10 rounded-2xl overflow-hidden" data-testid={`exercise-${i}`}>
                <div className="relative h-44 sm:h-full bg-sage">
                  <img src={exImage(ex.image)} alt={ex.name} className="absolute inset-0 w-full h-full object-cover" />
                </div>
                <div className="p-6 pt-2 sm:pt-6">
                  <div className="flex items-center justify-between">
                    <span className="text-xs tracking-[0.15em] uppercase text-clay font-semibold">{ex.target}</span>
                    <span className="text-xs text-forest/50">{ex.duration}</span>
                  </div>
                  <h3 className="mt-1 font-serif text-2xl">{ex.name}</h3>
                  <ol className="mt-3 space-y-2">
                    {ex.steps.map((s, k) => (
                      <li key={k} className="flex gap-3 text-sm text-forest/80">
                        <span className="w-5 h-5 shrink-0 rounded-full bg-sage text-clay text-xs flex items-center justify-center font-semibold">{k + 1}</span>
                        {s}
                      </li>
                    ))}
                  </ol>
                  <p className="mt-3 text-sm text-forest/60 italic border-l-2 border-clay/40 pl-3">{ex.benefit}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Routine */}
        <section className="mt-12">
          <h2 className="font-serif text-3xl">Tu rutina facial de hoy</h2>
          <div className="mt-5 inline-flex rounded-full bg-sage p-1">
            <button data-testid="routine-tab-morning" onClick={() => setTab("morning")} className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${tab === "morning" ? "bg-clay text-cream" : "text-forest/70"}`}>
              <Sun className="w-4 h-4" /> Mañana
            </button>
            <button data-testid="routine-tab-night" onClick={() => setTab("night")} className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${tab === "night" ? "bg-clay text-cream" : "text-forest/70"}`}>
              <Moon className="w-4 h-4" /> Noche
            </button>
          </div>
          <div className="mt-6 space-y-3" data-testid="routine-steps">
            {routine.map((step, i) => (
              <div key={i} className="flex gap-4 bg-white border border-forest/10 rounded-2xl p-5">
                <span className="w-8 h-8 shrink-0 rounded-full bg-clay text-cream flex items-center justify-center font-serif">{i + 1}</span>
                <div>
                  <h4 className="font-semibold text-forest">{step.product}</h4>
                  <p className="text-sm text-forest/70 mt-0.5">{step.action}</p>
                  <p className="text-xs text-clay mt-1 flex items-center gap-1.5"><Leaf className="w-3.5 h-3.5" /> {step.why}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Tip */}
        <div className="mt-10 flex gap-4 bg-sage rounded-2xl p-6">
          <Lightbulb className="w-6 h-6 text-clay shrink-0" />
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-clay">Consejo del día</p>
            <p className="mt-1 text-forest/80">{d.tip}</p>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-12 flex flex-col sm:flex-row items-center gap-4">
          <button data-testid="complete-day-button" onClick={toggleComplete} disabled={saving}
            className={`w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full font-medium px-8 py-4 transition-colors disabled:opacity-60 ${info.completed ? "bg-sage text-forest border border-clay/30" : "bg-clay text-cream hover:bg-clay-dark"}`}>
            <Check className="w-4 h-4" /> {info.completed ? "Completado — marcar pendiente" : "Marcar día como completado"}
          </button>
          <div className="flex items-center gap-3 sm:ml-auto">
            {dayNum > 1 && (
              <button data-testid="prev-day-button" onClick={() => navigate(`/programa/dia/${dayNum - 1}`)} className="inline-flex items-center gap-2 rounded-full border border-forest/15 px-5 py-3 text-sm hover:border-clay transition-colors">
                <ArrowLeft className="w-4 h-4" /> Anterior
              </button>
            )}
            {dayNum < 28 && (
              <button data-testid="next-day-button" onClick={() => navigate(`/programa/dia/${dayNum + 1}`)} className="inline-flex items-center gap-2 rounded-full border border-forest/15 px-5 py-3 text-sm hover:border-clay transition-colors">
                Siguiente <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
